import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { MatDialog } from '@angular/material/dialog';
import { InfoCardComponent } from 'src/app/components/shared/info-card/info-card.component';
import { GenerateMapService } from 'src/app/services/generate-map.service';
import { PlacesService } from 'src/app/services/places.service';
import { ActivatedRoute, Router } from '@angular/router';
import { TicketsService } from 'src/app/services/tickets.service';
import { SeatMapService } from 'src/app/services/seat-map.service';

@Component({
  selector: 'app-map-venue',
  templateUrl: './map-venue.component.html',
  styleUrls: ['./map-venue.component.scss']
})
export class MapVenueComponent implements OnChanges {
  @Input() venue!: any;
  map!: any;
  capacity!: any;
  business = JSON.parse(localStorage.getItem('business') || '{}');

  openedCreateMap: boolean = false;
  editCapacity: boolean = false;
  blockedSeats: any[] = [];
  isSuperAdmin: boolean = false;
  fromCreate: boolean = false;
  isEditingMap: boolean = false;
  isSaving: boolean = false;

  constructor(
    private placeService: PlacesService,
    private dialog: MatDialog,
    private generateMapService: GenerateMapService,
    private auth: AuthenticationService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private ticketService: TicketsService,
    private seatMapService: SeatMapService
  ) { }

  ngAfterViewInit(): void {
    this.fromCreate = location.pathname.includes('/create-map/');
    this.isEditingMap = this.fromCreate;
    const idVenue = +this.activatedRoute.snapshot.params['idVenue'];
    if (!idVenue) {
      if (this.venue) {
        this.loadFromVenue(this.venue);
      }
      // else: wait for ngOnChanges to fire when venue @Input arrives
      return;
    }
    this.placeService.getPlaceById(idVenue).subscribe({
      next: (res: any) => {
        this.venue = res.data;
        this.capacity = res.data.capacity;
        this.map = res.data.map;
        this.blockedSeats = res.data.blocked_seats || res.data.blockedSeats || [];
        this.isSuperAdmin = this.auth.isSuperAdmin();
        if (this.fromCreate) {
          this.openedCreateMap = true;
        }
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['venue'] && this.venue) {
      const idVenue = +this.activatedRoute.snapshot.params['idVenue'];
      if (!idVenue) {
        this.loadFromVenue(this.venue);
      }
    }
  }

  loadFromVenue(venue: any) {
    this.map = venue.map;
    this.capacity = venue.capacity;
    this.blockedSeats = venue.blocked_seats || venue.blockedSeats || [];
    this.isSuperAdmin = this.auth.isSuperAdmin();
  }

  createMap() {
    this.openedCreateMap = !this.openedCreateMap;
    let action = !this.openedCreateMap ? 'close' : 'edit';
    this.placeService.setActionMap({ action: action });
  }

  editMap() {
    const idVenue = this.venue?.id || +this.activatedRoute.snapshot.params['idVenue'];
    this.router.navigate(['/panel-control/config/venues/create-map', idVenue]);
  }

  startEditMap() {
    this.isEditingMap = true;
  }

  ngOnDestroy(): void {
    this.placeService.setActionMap(null);
  }

  changeCapacity() {
    if (this.editCapacity) {
      this.placeService.updateVenue(this.venue.id, { capacity: this.capacity }).subscribe({
        next: (result) => {
          this.openInfoCard('Los cambios se han guardado correctamente.', false);
        },
        error: (err) => {
          this.openInfoCard('¡Vaya! No se han podido guardar los cambios, inténtalo de nuevo más tarde.');
        }
      });
    }
    this.editCapacity = !this.editCapacity;
  }

  saveMapVenue() {
    const isNewMap = !this.map;
    const savedMap = this.generateMapService.generatePayloadMap();

    const payloadEditMap = {
      map_configuration: savedMap.configuration,
      map_sections: savedMap.sections,
      map_levels: savedMap.levels,
      map_seats: savedMap.seats,
      zone_navigation: savedMap.zoneNavigation,
      map_nav_preview: savedMap.navPreview,
      legend_config: savedMap.legendConfig,
    };

    this.isSaving = true;
    this.placeService.updateImage(this.venue.id, payloadEditMap).subscribe({
      next: () => {
        this.isSaving = false;
        this.openInfoCard('¡Mapa guardado correctamente!', false);
        this.placeService.clearPlaceCache();
        this.placeService.getPlaceById(this.venue.id).subscribe({
          next: (res: any) => {
            this.map = res.data.map;
            this.openedCreateMap = false;

            if (isNewMap && savedMap.levels?.length > 0) {
              const version = res.data?.current_version || res.data?.map?.version;
              if (version) {
                this.seatMapService.getLevelsVersionMap(this.venue.id, version).subscribe({
                  next: (levelsRes: any) => {
                    this.createDefaultTickets(levelsRes.data || []);
                  }
                });
              }
            }
          }
        });
      },
      error: () => {
        this.isSaving = false;
        this.openInfoCard('¡Vaya! No se ha podido guardar el mapa.', true);
      }
    });
  }

  private createDefaultTickets(mapLevels: any[]) {
    if (mapLevels.length === 0) return;

    const ticketLevels = mapLevels.map((level: any) => ({
      id: level.id,
      price: 0,
      name: level.name,
      color: level.color
    }));

    const ticketPayload = {
      name: 'Entrada general',
      active: true,
      price: 0,
      only_tpv_sales: false,
      special_seats: false,
      levels: ticketLevels,
      sessions: [],
      seats: []
    };

    this.ticketService.createTicketForSpace(this.venue.id, ticketPayload).subscribe({
      next: () => {
        console.log('Entrada general creada automáticamente para el recinto');
      },
      error: (err) => {
        console.error('Error al crear entrada general:', err);
      }
    });
  }

  regenerateDefaultTickets() {
    const version = this.venue?.current_version || this.map?.version;
    if (!version) {
      this.openInfoCard('No se pudo obtener la versión del mapa.', true);
      return;
    }

    this.ticketService.getTicketsBySpace(this.venue.id).subscribe({
      next: (res: any) => {
        const tickets = res.data || [];
        const existingGeneral = tickets.filter((t: any) => t.name === 'Entrada general');

        const deleteAndRecreate = () => {
          this.seatMapService.getLevelsVersionMap(this.venue.id, version).subscribe({
            next: (levelsRes: any) => {
              const levels = levelsRes.data || [];
              if (levels.length === 0) {
                this.openInfoCard('No se encontraron niveles en el mapa.', true);
                return;
              }
              this.createDefaultTickets(levels);
              this.openInfoCard('Entrada general regenerada correctamente.', false);
            },
            error: () => {
              this.openInfoCard('Error al obtener los niveles del mapa.', true);
            }
          });
        };

        if (existingGeneral.length > 0) {
          // Borrar TODAS las "Entrada general" existentes antes de recrear
          let deleted = 0;
          existingGeneral.forEach((t: any) => {
            this.ticketService.deleteTicket(t.id).subscribe({
              next: () => {
                deleted++;
                if (deleted === existingGeneral.length) {
                  deleteAndRecreate();
                }
              },
              error: () => {
                deleted++;
                if (deleted === existingGeneral.length) {
                  deleteAndRecreate();
                }
              }
            });
          });
        } else {
          deleteAndRecreate();
        }
      },
      error: () => {
        this.openInfoCard('Error al obtener las entradas del recinto.', true);
      }
    });
  }

  openInfoCard(msg: string, icon: boolean = true) {
    const dialogRef = this.dialog.open(InfoCardComponent, {
      panelClass: 'info-dialog',
      data: {
        icon: icon,
        text: msg,
        btnCancel: false
      }
    });
  }
}
